import { useMemo } from 'react';
import {
  IconInfoCircle,
  IconMap,
  IconListCheck,
  IconLink,
  IconMessageCircle,
  IconPaperclip,
  IconHistory,
  IconNote,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { CollapsibleTabItem } from '@/components/common/CollapsibleTabs';

interface SectionTabsOptions {
  showRightPanel: boolean;
  hasNotes: boolean;
  notesLoading: boolean;
  hasAttachments: boolean;
  hasRequirements: boolean;
  hasRelatedTasks: boolean;
  hasGeometries: boolean;
  /**
   * Whether comments are available — the tab shows only when both the context
   * permits comments (always in the authenticated view; `config.showComments`
   * in a public share) AND at least one comment exists. When `false`, the tab
   * is hidden entirely.
   */
  hasComments?: boolean;
  /** Hides the history tab (public share — history is never exposed). */
  includeHistory?: boolean;
}

export function useTaskDetailsSectionTabs({
  showRightPanel,
  hasNotes,
  notesLoading,
  hasAttachments,
  hasRequirements,
  hasRelatedTasks,
  hasGeometries,
  hasComments = true,
  includeHistory = true,
}: SectionTabsOptions): CollapsibleTabItem[] {
  const { t } = useTranslation();

  return useMemo<CollapsibleTabItem[]>(
    () => [
      {
        id: 'general',
        label: t('taskDetails.tabs.general'),
        icon: IconInfoCircle,
      },
      ...(hasGeometries
        ? [
            {
              id: 'mapRepresentation',
              label: t('taskDetails.tabs.mapRepresentation'),
              icon: IconMap,
            },
          ]
        : []),
      ...(hasRequirements
        ? [
            {
              id: 'requirements',
              label: t('taskDetails.tabs.requirements'),
              icon: IconListCheck,
            },
          ]
        : []),
      ...(hasRelatedTasks
        ? [
            {
              id: 'relatedTasks',
              label: t('taskDetails.tabs.relatedTasks'),
              icon: IconLink,
            },
          ]
        : []),
      ...(!showRightPanel && hasComments
        ? [
            {
              id: 'comments',
              label: t('taskDetails.tabs.comments'),
              icon: IconMessageCircle,
            },
          ]
        : []),
      ...(!notesLoading && hasNotes
        ? [
            {
              id: 'notes',
              label: t('taskDetail.tabs.notes'),
              icon: IconNote,
            },
          ]
        : []),
      ...(hasAttachments
        ? [
            {
              id: 'attachments',
              label: t('taskDetails.tabs.attachments'),
              icon: IconPaperclip,
            },
          ]
        : []),
      ...(includeHistory
        ? [
            {
              id: 'history',
              label: t('taskDetails.tabs.history'),
              icon: IconHistory,
            },
          ]
        : []),
    ],
    [
      t,
      hasGeometries,
      hasRequirements,
      hasRelatedTasks,
      hasAttachments,
      showRightPanel,
      hasNotes,
      notesLoading,
      hasComments,
      includeHistory,
    ]
  );
}
